'use client';

import type { ReactNode } from 'react';
import { Tooltip } from '@/components/ui/tooltip';
import { stateChips } from '@/domain/state-chips';
import { glyphReading, type GlyphStates } from '@/domain/tags';
import { useT } from '@/lib/locale-context';
import { HealthValue } from './health-value';
import { LifecycleValue } from './lifecycle-value';
import styles from './values.module.css';

/**
 * The item glyph's tip (2026-10-14): hovering the dot says what it sums.
 * First two sentences, one per axis of the mark (`glyphReading`: where the
 * work stands, then whether a health state is flagged), so every dot explains
 * itself, an idle one included. Then the states themselves as values - the
 * health states the item wears, worst first, and the lifecycle chips in their
 * chronological order; rest is silent there as on the status line
 * (`stateChips`, R1). The same tip on every surface that draws the dot, so an
 * item's states are one hover away wherever the item is named.
 *
 * The verdict is not among the states: the glyph carries the atoms alone, and
 * a denial already wears its health state.
 *
 * The wrapper adds no box (`Tooltip`), so the host keeps its own dot markup.
 */
export function GlyphTip({
  states,
  children,
  placement,
}: {
  states: GlyphStates;
  children: ReactNode;
  placement?: 'top' | 'bottom';
}) {
  const t = useT();
  const [where, health] = glyphReading(states);
  const chips = stateChips({ ...states.standings, verdict: 'open' });
  const listed = states.worn.length > 0 || chips.length > 0;
  return (
    <Tooltip
      placement={placement}
      content={
        <span className={styles.glyphTip} data-testid="glyph-tip">
          <span>
            {t.msg(where)} {t.msg(health)}
          </span>
          {listed && (
            <span className={styles.glyphStates} data-testid="glyph-states">
              {states.worn.map((w, i) => (
                <HealthValue key={`${w.level}:${i}`} state={w} />
              ))}
              {chips.map((chip) => (
                <LifecycleValue key={chip.kind} chip={chip} size="sm" />
              ))}
            </span>
          )}
        </span>
      }
    >
      {children}
    </Tooltip>
  );
}
